import { useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { IconClose } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  PendingAnnualitiesResponse,
  PendingAnnualityRow,
  SubscriptionDto,
} from "../../../types/subscription";

type Props = {
  subscription: SubscriptionDto;
  onClose: () => void;
  onApplied: () => void;
};

const fmtMoney = (n: number): string =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(n);

const fmtDate = (iso: string): string => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const parseAmount = (raw: string): number => Number(raw.replace(",", "."));

export default function PendingAnnualitiesDrawer({
  subscription,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<PendingAnnualitiesResponse | null>(null);
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    request<PendingAnnualitiesResponse>(
      `/api/admin/operations/subscriptions/${subscription.id}/pending-annualities`,
      { token },
    )
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setAmounts({});
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof HttpError
            ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
            : "No se pudieron cargar los años pendientes",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription.id, token]);

  const filledRows = (data?.pending ?? []).filter(
    (row) => (amounts[row.annuality] ?? "").trim() !== "",
  );

  const validate = (): string | null => {
    if (filledRows.length === 0) {
      return "Introduce el importe anual de al menos un año";
    }
    for (const row of filledRows) {
      const value = parseAmount(amounts[row.annuality]);
      if (!Number.isFinite(value) || value <= 0) {
        return `Año ${row.annuality}: introduce un importe anual válido (> 0)`;
      }
    }
    return null;
  };

  const review = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const submit = async () => {
    if (submitting || !data) return;
    setSubmitting(true);
    setError(null);
    try {
      await request(
        `/api/admin/operations/subscriptions/${subscription.id}/pending-annualities`,
        {
          method: "POST",
          token,
          body: {
            annualities: filledRows.map((row) => ({
              annuality: row.annuality,
              annualAmount: parseAmount(amounts[row.annuality]),
            })),
          },
        },
      );
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Actualizar los importes falló",
      );
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const pending = data?.pending ?? [];
  const isMonthly = data?.frequency === "MONTHLY";

  return (
    <DrawerShell title="Actualizar importes por año" onClose={onClose}>
      {loading && (
        <p style={{ margin: 0, color: "#73879C", fontSize: ".85rem" }}>Cargando…</p>
      )}

      {!loading && loadError && <ErrorBox>{loadError}</ErrorBox>}

      {!loading && !loadError && data && pending.length === 0 && (
        <div
          style={{
            padding: "0.85rem 1rem",
            background: "#d4edda",
            color: "#155724",
            borderRadius: "0.5rem",
            fontSize: ".85rem",
          }}
        >
          No hay años con recibos futuros modificables.
        </div>
      )}

      {!loading && !loadError && data && pending.length > 0 && (
        <>
          <p style={{ margin: "0 0 0.75rem", color: "#73879C", fontSize: ".82rem" }}>
            Suscripción <strong>{data.frequency}</strong> ({data.carrier}), efecto{" "}
            {fmtDate(data.effectDate)}, anualidad en vigor {data.currentAnnuality}. Introduce el{" "}
            <strong>importe anual</strong> de la tabla del asegurador solo en los años que quieras
            actualizar: se modifican únicamente los <strong>recibos futuros</strong>
            {isMonthly ? " (cada recibo pasa a importe/12)" : ""} y su desglose se recalcula.
          </p>
          <div style={{ display: "grid", gap: "0.7rem" }}>
            {pending.map((row) => (
              <PendingYearInput
                key={row.annuality}
                row={row}
                isMonthly={isMonthly}
                value={amounts[row.annuality] ?? ""}
                onChange={(value) =>
                  setAmounts((s) => ({ ...s, [row.annuality]: value }))
                }
              />
            ))}
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <DrawerFooter
            onClose={onClose}
            onSubmit={review}
            submitting={submitting}
            submitLabel={`Revisar cambios${filledRows.length > 0 ? ` (${filledRows.length})` : ""}`}
          />
        </>
      )}

      {confirmOpen && data && (
        <ConfirmModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={submit}
          submitting={submitting}
          isMonthly={isMonthly}
          rows={filledRows.map((row) => ({
            annuality: row.annuality,
            currentAmount: row.currentAnnualAmount,
            newAmount: parseAmount(amounts[row.annuality]),
            futureInvoices: row.futureInvoices,
            activeInvoices: row.activeInvoices,
          }))}
        />
      )}
    </DrawerShell>
  );
}

function PendingYearInput({
  row,
  isMonthly,
  value,
  onChange,
}: {
  row: PendingAnnualityRow;
  isMonthly: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const parsed = parseAmount(value);
  const showMonthly = isMonthly && value.trim() !== "" && Number.isFinite(parsed) && parsed > 0;
  return (
    <div
      style={{
        background: "#f8f9fb",
        border: "1px solid #e9ecef",
        borderRadius: "0.5rem",
        padding: "0.7rem 0.85rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.45rem",
          flexWrap: "wrap",
        }}
      >
        <strong style={{ fontSize: ".82rem", color: "#1f2933" }}>
          Año {row.annuality}
        </strong>
        <span
          style={{
            fontSize: ".68rem",
            fontWeight: 700,
            padding: "0.05rem 0.4rem",
            background: "#e7f1fd",
            color: "#0069d9",
            borderRadius: "0.25rem",
            letterSpacing: ".3px",
            textTransform: "uppercase",
          }}
        >
          {row.futureInvoices} de {row.activeInvoices} recibos futuros
        </span>
        <span style={{ marginLeft: "auto", fontSize: ".75rem", color: "#73879C" }}>
          {fmtDate(row.periodStart)} → {fmtDate(row.periodEnd)}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "0.75rem" }}>
        <label style={{ display: "grid", gap: "0.3rem", flex: 1 }}>
          <span
            style={{
              fontSize: ".72rem",
              color: "#73879C",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: ".3px",
            }}
          >
            Nuevo importe anual
          </span>
          <input
            type="number"
            value={value}
            min={0}
            step={0.01}
            placeholder={`Actual: ${fmtMoney(row.currentAnnualAmount)}`}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%",
              padding: "0.45rem 0.65rem",
              borderRadius: "0.4rem",
              border: "1px solid #dee2e6",
              background: "white",
              fontSize: ".95rem",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </label>
        <div style={{ fontSize: ".75rem", color: "#73879C", paddingBottom: "0.5rem" }}>
          {showMonthly
            ? `≈ ${fmtMoney(Math.round((parsed / 12) * 100) / 100)}/mes`
            : `Anual actual: ${fmtMoney(row.currentAnnualAmount)}`}
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({
  rows,
  isMonthly,
  onCancel,
  onConfirm,
  submitting,
}: {
  rows: Array<{
    annuality: number;
    currentAmount: number;
    newAmount: number;
    futureInvoices: number;
    activeInvoices: number;
  }>;
  isMonthly: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const totalInvoices = rows.reduce((acc, row) => acc + row.futureInvoices, 0);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "0.6rem",
          padding: "1.1rem 1.2rem",
          width: "min(520px, 100%)",
          boxShadow: "0 20px 50px rgba(16, 30, 54, .18)",
        }}
      >
        <h3 style={{ margin: "0 0 0.6rem", fontSize: "1rem", color: "#1f2933" }}>
          Confirma los nuevos importes anuales
        </h3>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 0.85rem" }}>
          {rows.map((row) => (
            <li
              key={row.annuality}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto 1fr",
                alignItems: "center",
                gap: ".6rem",
                padding: "0.5rem 0.7rem",
                background: "#f6f8fc",
                borderRadius: "0.45rem",
                marginBottom: ".35rem",
                fontSize: ".85rem",
              }}
            >
              <strong style={{ color: "#2A3F54" }}>
                Año {row.annuality}
                <span
                  style={{
                    display: "block",
                    fontSize: ".68rem",
                    fontWeight: 600,
                    color: "#73879C",
                  }}
                >
                  {row.futureInvoices}/{row.activeInvoices} recibos
                </span>
              </strong>
              <span
                style={{
                  color: "#73879C",
                  textAlign: "right",
                  textDecoration: "line-through",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtMoney(row.currentAmount)}
              </span>
              <span style={{ color: "#0069d9", textAlign: "center" }}>→</span>
              <span
                style={{
                  color: "#0069d9",
                  fontWeight: 700,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtMoney(row.newAmount)}
                {isMonthly && (
                  <span
                    style={{
                      display: "block",
                      fontSize: ".68rem",
                      fontWeight: 600,
                      color: "#73879C",
                    }}
                  >
                    {fmtMoney(Math.round((row.newAmount / 12) * 100) / 100)}/mes
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        <p
          style={{
            fontSize: ".78rem",
            color: "#856404",
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            margin: 0,
          }}
        >
          Se actualizarán <strong>{totalInvoices} recibos futuros</strong>
          {isMonthly ? " (importe anual / 12 cada uno)" : ""}. Los recibos ya
          cobrados o vencidos no se tocan. El desglose (fees/impuestos) se
          recalcula proporcionalmente.
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            justifyContent: "flex-end",
            marginTop: "0.95rem",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={ghostBtn}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            style={primaryBtn}
          >
            {submitting ? "Actualizando…" : "Actualizar importes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100vw)",
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function DrawerFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        marginTop: "1.1rem",
      }}
    >
      <button type="button" onClick={onClose} style={ghostBtn} disabled={submitting}>
        Cancelar
      </button>
      <button type="button" onClick={onSubmit} style={primaryBtn} disabled={submitting}>
        {submitting ? "Aplicando…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};
